import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageFooter from "@/components/PageFooter";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col">
      {/* Header */}
      <header className="glass-card border-b border-border/30 sticky top-0 z-10 shadow-elegant">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoDark} alt="untabbed.to logo" className="h-8 w-8 dark:hidden" />
              <img src={logo} alt="untabbed.to logo" className="h-8 w-8 hidden dark:block" />
              <h1 className="text-3xl font-display font-bold text-black dark:text-white tracking-tight">
                untabbed.to
              </h1>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 flex-1 max-w-4xl">
        <div className="glass-card shadow-card p-8 rounded-lg">
          <h2 className="text-3xl font-display font-bold text-foreground mb-6">Terms of Service</h2>
          <p className="text-sm text-muted-foreground mb-6">Last updated: November 11, 2025</p>

          <div className="space-y-6 text-foreground">
            <section>
              <h3 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h3>
              <p className="text-muted-foreground leading-relaxed">
                By accessing and using the Untabbed.to website and Chrome extension ("Service"), you accept and agree to be bound by
                the terms and provisions of this agreement. If you do not agree to abide by these terms, please do not use this Service.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">2. Service Description</h3>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">2.1 Local-First Storage</h4>
              <p className="text-muted-foreground leading-relaxed">
                The Untabbed.to Chrome extension stores all captured tab collections <strong>locally in your browser's storage</strong> by default.
                This data remains on your device and is not automatically transmitted to our servers.
              </p>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">2.2 Optional Cloud Sharing</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You may optionally choose to share your tab collections using the "Share" feature, which:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>Uploads the selected collection to our cloud service (untabbed.to)</li>
                <li>Generates a unique, publicly accessible URL</li>
                <li>Makes the collection viewable by anyone with the URL</li>
                <li>Is entirely voluntary and user-initiated</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                <strong>You are responsible for:</strong> Not sharing collections containing sensitive, confidential, or private information
                that you do not wish to make publicly accessible.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">3. Use License</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Permission is granted to use Untabbed.to for personal and commercial purposes, subject to the following restrictions:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1 ml-4">
                <li>You may not modify, copy, or distribute the extension software</li>
                <li>You may not attempt to decompile or reverse engineer any software</li>
                <li>You may not remove any copyright or proprietary notices</li>
                <li>You may not use the Service to share illegal, harmful, or malicious content</li>
                <li>You may not abuse the Service through automated tools, excessive requests, or security vulnerabilities</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">4. User Content and Shared Collections</h3>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.1 Your Content</h4>
              <p className="text-muted-foreground leading-relaxed mb-3">
                When you share a collection, you retain all rights to your content. However, by sharing, you grant Untabbed.to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>A worldwide, non-exclusive, royalty-free license to host, store, and display the shared collection</li>
                <li>The right to make the collection publicly accessible via the generated URL</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.2 Content Responsibility</h4>
              <p className="text-muted-foreground leading-relaxed">
                You are solely responsible for the content you share. You agree not to share collections that:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>Contain illegal content or violate applicable laws</li>
                <li>Infringe on intellectual property rights of others</li>
                <li>Contain malicious URLs, phishing links, or malware</li>
                <li>Contain personal information of others without their consent</li>
                <li>Are intended to harass, abuse, or harm others</li>
              </ul>

              <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">4.3 Content Removal</h4>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to remove any shared collection that violates these terms or applicable law,
                without notice. To request deletion of a shared collection, contact us at <a href="mailto:contact@opendeluxe.com" className="text-primary hover:text-primary-glow transition-colors">contact@opendeluxe.com</a>.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">5. Privacy</h3>
              <p className="text-muted-foreground leading-relaxed">
                Your use of this Service is also governed by our Privacy Policy. Please review our{" "}
                <a href="/privacy-policy" className="text-primary hover:text-primary-glow transition-colors">Privacy Policy</a>{" "}
                to understand our data practices, particularly regarding local storage and optional cloud sharing.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">6. Platform Provider Disclaimer</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                <strong>Untabbed.to is a platform provider only.</strong> We provide the technical infrastructure to enable users
                to capture and optionally share their browser tab collections. We do not:
              </p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>Monitor, review, or control the content of shared collections</li>
                <li>Endorse, verify, or guarantee the accuracy, legality, or safety of shared content</li>
                <li>Take responsibility for user-generated content or URLs shared in collections</li>
                <li>Guarantee that shared collections are free from illegal, harmful, or malicious content</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                <strong>Users are solely responsible</strong> for the content they share. If someone shares criminal, illegal,
                or harmful content using our Service, the responsibility lies entirely with the user who shared it, not with
                Untabbed.to. We act as a neutral technical service provider under the EU E-Commerce Directive (Articles 12-15)
                and similar safe harbor provisions.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-3">
                We will cooperate with law enforcement and remove content when legally required or when we become aware of
                clearly illegal content, but we have no general obligation to monitor user content.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">7. Service Disclaimer</h3>
              <p className="text-muted-foreground leading-relaxed">
                The Service is provided on an 'as is' and 'as available' basis. Untabbed.to makes no warranties, expressed or implied,
                and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions
                of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
                We do not warrant that the Service will be uninterrupted, timely, secure, or error-free.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">8. Limitation of Liability</h3>
              <p className="text-muted-foreground leading-relaxed">
                In no event shall Untabbed.to or its suppliers be liable for any damages (including, without limitation, damages for loss of data,
                loss of privacy, or profit, or due to business interruption) arising out of the use or inability to use the Service,
                even if Untabbed.to or an authorized representative has been notified orally or in writing of the possibility of such damage.
                This includes but is not limited to damages arising from sharing collections, loss of locally stored data, unauthorized
                access to shared collections, or any illegal or harmful content shared by users.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-3">
                <strong>We are not liable for user-generated content.</strong> As a platform provider, we are not responsible for
                any damages, losses, or legal consequences arising from content shared by users, including criminal content, copyright
                infringement, defamation, or any other illegal or harmful material.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">9. Service Modifications</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time,
                with or without notice. We may also impose limits on certain features or restrict access to parts of the Service
                without notice or liability. We are not liable for any modification, suspension, or discontinuance of the Service.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">10. Changes to Terms</h3>
              <p className="text-muted-foreground leading-relaxed">
                We may update these Terms of Service from time to time. The updated version will be indicated by an updated
                "Last updated" date at the top of this page. Continued use of the Service after changes constitutes acceptance
                of the updated terms. We encourage you to review these terms periodically.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">11. Governing Law</h3>
              <p className="text-muted-foreground leading-relaxed">
                These terms and conditions are governed by and construed in accordance with the laws of the Federal Republic of Germany,
                without regard to its conflict of law rules, and you irrevocably submit to the exclusive jurisdiction of the courts in Berlin, Germany.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">12. Contact Information</h3>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at:<br />
                <strong>opendeluxe UG</strong><br />
                Teltower Damm 159<br />
                14167 Berlin, Germany<br />
                Email: <a href="mailto:contact@opendeluxe.com" className="text-primary hover:text-primary-glow transition-colors">contact@opendeluxe.com</a><br />
                Phone: <a href="tel:+4915678521554" className="text-primary hover:text-primary-glow transition-colors">+49(0)15678-521554</a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default Terms;
